import React, { createContext, useState, useEffect, useMemo } from 'react'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { getTheme } from './muiTheme'

// Create the context
export const ThemeContext = createContext({
  mode: 'system',
  setMode: () => {},
})

export const ThemeProvider = ({ children }) => {
  // Get the initial mode from localStorage or default to 'system'
  const [mode, setMode] = useState(() => {
    return localStorage.getItem('themeMode') || 'system'
  })

  // Determine the actual theme based on mode and system preference
  const [actualTheme, setActualTheme] = useState(() => {
    if (mode === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
    }
    return mode
  })

  // Listen for changes in system preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = () => {
      if (mode === 'system') {
        setActualTheme(mediaQuery.matches ? 'dark' : 'light')
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [mode])

  // Update the actual theme when mode changes
  useEffect(() => {
    if (mode === 'system') {
      const isDarkMode = window.matchMedia(
        '(prefers-color-scheme: dark)',
      ).matches
      setActualTheme(isDarkMode ? 'dark' : 'light')
    } else {
      setActualTheme(mode)
    }
  }, [mode])

  // Create the MUI theme based on the actual theme
  const theme = useMemo(() => getTheme(actualTheme), [actualTheme])

  // Create the context value
  const contextValue = useMemo(
    () => ({
      mode,
      setMode: (newMode) => {
        setMode(newMode)
        localStorage.setItem('themeMode', newMode)
      },
    }),
    [mode],
  )

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  )
}
